import { msalInstance, apiScope } from "../auth/msal";
import { config as runtimeConfig } from "../runtime-config";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// True while a sign-out is already in flight, so the many parallel React Query
// consumers who all hit 401 on the same expired token don't each call
// logoutRedirect and race. First caller wins, everyone else short-circuits.
let signOutInFlight = false;

/**
 * Force a clean sign-out and let msal-react's <AuthenticatedTemplate> switch
 * back to <SignIn> on the next render.
 *
 * We call `logoutRedirect` with `onRedirectNavigate: () => false` — that skips
 * the round-trip to login.microsoftonline.com/{tenant}/oauth2/v2.0/logout but
 * still clears MSAL's local caches and dispatches LOGOUT_END, which is what
 * useIsAuthenticated() listens for. The user's Microsoft session stays alive,
 * so clicking "Sign in" again on the SignIn screen re-authenticates via SSO
 * without asking for a password.
 *
 * Used when the token has expired past the point where a silent refresh can
 * recover (silent failure, or backend rejects with 401 anyway). Trying an
 * interactive redirect mid-fetch tends to leave the SPA in limbo — the browser
 * may block the redirect since it wasn't user-initiated, React Query keeps
 * spinning, and the user sees a generic error with no way to recover.
 */
async function forceSignOut(): Promise<never> {
  if (!signOutInFlight) {
    signOutInFlight = true;
    const account =
      msalInstance.getActiveAccount() ??
      msalInstance.getAllAccounts()[0] ??
      null;
    try {
      await msalInstance.logoutRedirect({
        account,
        onRedirectNavigate: () => false,
      });
    } catch (e) {
      console.error("[MSAL] logoutRedirect failed, hard reload", e);
      window.location.reload();
    }
  }
  // The caller's promise chain must abort — throwing an ApiError propagates
  // to React Query's onError. We never return; the SPA re-renders as SignIn.
  throw new ApiError(401, "session_expired");
}

async function getAccessToken(): Promise<string> {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return forceSignOut();

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: [apiScope],
      account,
    });
    return response.accessToken;
  } catch (e) {
    // Silent acquisition can fail for many reasons: session aged out, consent
    // required, MFA required, refresh token expired, network hiccup. In every
    // one of those, the safest UX is to bring the user back to the SignIn
    // screen — an interactive redirect fired from inside a fetch handler often
    // gets blocked, and even when it works it leaves stale React Query state
    // behind. Clean local sign-out + a fresh SSO-backed sign-in is faster and
    // more predictable.
    console.warn("[MSAL] silent token acquisition failed, signing out", e);
    return forceSignOut();
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const base = runtimeConfig.apiBaseUrl;
  // SPA-side fetch to its own backend; base is runtime-injected via window.__WHOSOUT_CONFIG__,
  // path is caller-controlled with hard-coded routes (not user input).
  // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
  const res = await fetch(`${base}/api${path}`, { ...init, headers });
  if (res.status === 401) {
    // Backend rejected the token even though MSAL considered it fresh —
    // revoked, audience mismatch, tenant restriction, clock skew. Same
    // escape hatch: force a clean sign-out.
    return forceSignOut();
  }
  if (!res.ok) {
    throw new ApiError(res.status, `api_${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export { ApiError };
